import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Upload, Trash2, Eye, CheckCircle2, Wallet, AlertTriangle } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney } from "@/utils/currency";
import { ProofModal } from "@/components/admin/ProofModal";
import { WorkerPayoutSection } from "@/components/admin/WorkerPayoutSection";
import { slotRows } from "@/constants/paymentMethods";

const METHODS = ["PayPal", "Bank transfer", "Wise", "Crypto", "Cash App", "Other"];

/**
 * Owner payment workspace — used from Payments history and the Submission review card.
 * Base + Bonus = Total is the single amount every summary uses. Bonus is editable while
 * the payment is owed; once paid it is finalized (changes then only via the explicit
 * reconciliation review). Proof of payment: upload/replace/remove/view — owner-only.
 */
export const PaymentModal = ({ payment, onClose, onChanged }) => {
  const [p, setP] = useState(payment);
  const [bonus, setBonus] = useState(String(payment.bonus ?? 0));
  const [method, setMethod] = useState(payment.method || "");
  const [txn, setTxn] = useState(payment.transaction_id || "");
  const [note, setNote] = useState(payment.note || "");
  const [paidAt, setPaidAt] = useState((payment.paid_at || new Date().toISOString()).slice(0, 10));
  const [payoutVia, setPayoutVia] = useState(payment.payout_method?.slot || "");
  const [payout, setPayout] = useState(null); // worker's saved payment details (owner view)
  const [proofFile, setProofFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewProof, setViewProof] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Always hydrate from the authoritative record by id — callers may pass a partial object
  // ({id}) or a cached list row; the modal must open THE current payment, every time.
  useEffect(() => {
    if (!payment?.id) { setHydrated(true); return; }
    axios.get(`${API}/admin/payments/${payment.id}`, { headers: authHeaders() })
      .then((r) => {
        const f = r.data;
        setP(f);
        setBonus(String(f.bonus ?? 0));
        setMethod(f.method || "");
        setTxn(f.transaction_id || "");
        setNote(f.note || "");
        setPaidAt((f.paid_at || new Date().toISOString()).slice(0, 10));
        setPayoutVia(f.payout_method?.slot || "");
      })
      .catch(() => toast.error("Could not load the payment record — close and retry."))
      .finally(() => setHydrated(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.id]);

  const owed = p.status === "owed";
  const total = (p.base ?? 0) + (parseFloat(bonus) || 0);

  // surface the worker's saved payout rails directly in the payment workflow (owner-only endpoint)
  useEffect(() => {
    if (!p.worker_id) return;
    axios.get(`${API}/admin/workers/${p.worker_id}/payment-details`, { headers: authHeaders() })
      .then((r) => setPayout(r.data))
      .catch(() => setPayout({ status: "unavailable" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.worker_id]);

  // primary is the recommended rail: pre-select it for owed payments once details load
  // (never overrides an already-recorded snapshot on paid payments)
  useEffect(() => {
    if (payout && payout.primary_complete && owed && !p.payout_method && !payoutVia) {
      setPayoutVia("primary");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payout]);

  const inputCls = "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none";
  const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]";

  const refresh = (next) => { setP(next); onChanged?.(); };

  const saveBonus = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/payments/${p.id}`, { bonus: parseFloat(bonus) || 0 }, { headers: authHeaders() });
      refresh(r.data);
      toast.success(`Bonus updated — total is now ${fmtMoney(r.data.amount)}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the bonus.");
    } finally { setBusy(false); }
  };

  const saveDetails = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/payments/${p.id}`, {
        method, transaction_id: txn, note,
        ...(p.status === "paid" && paidAt ? { paid_at: new Date(paidAt).toISOString() } : {}),
      }, { headers: authHeaders() });
      refresh(r.data);
      toast.success("Payment details saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save details.");
    } finally { setBusy(false); }
  };

  const uploadProof = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await axios.post(`${API}/admin/payments/${p.id}/proof`, fd, { headers: authHeaders() });
    refresh(r.data);
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      if (proofFile) await uploadProof(proofFile);
      await axios.post(`${API}/admin/payments/${p.id}/mark-paid`, {
        method: method || undefined, transaction_id: txn || undefined, note: note || undefined,
        paid_at: paidAt ? new Date(paidAt).toISOString() : undefined,
        payout_via: payoutVia || undefined,
      }, { headers: authHeaders() });
      toast.success("Payment recorded as paid.");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark as paid.");
    } finally { setBusy(false); }
  };

  const removeProof = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/payments/${p.id}/proof`, { headers: authHeaders() });
      setP((x) => ({ ...x, has_proof: false, proof_filename: null, proof_content_type: null }));
      onChanged?.();
      toast.success("Proof removed.");
    } catch { toast.error("Could not remove proof."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="payment-modal" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!p.status ? (
          <div data-testid="payment-modal-loading" className="flex items-center justify-center gap-2 py-16 text-sm font-medium text-[var(--pv-text-4)]">
            <Loader2 className="h-4 w-4 animate-spin text-cyan-400" /> Loading payment record...
          </div>
        ) : (
        <>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">{p.worker_name || "Worker"}</h2>
            <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">{p.job_title || "Manual payment"}</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="payment-modal-status" className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${owed ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{p.status}</span>
            <button data-testid="payment-modal-close" onClick={onClose} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]" aria-label="Close"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {/* amounts — the same base/bonus/total every summary uses */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="rounded-2xl bg-[var(--pv-subtle)] p-3.5">
            <p className={labelCls}>Base pay</p>
            <p data-testid="payment-base" className="font-display text-lg font-bold text-[var(--pv-text)]">{fmtMoney(p.base)}</p>
          </div>
          <div className="rounded-2xl bg-[var(--pv-subtle)] p-3.5">
            <p className={labelCls}>Bonus</p>
            {owed ? (
              <div className="flex items-center gap-1.5">
                <input data-testid="payment-bonus-input" type="number" min="0" step="0.01" value={bonus} onChange={(e) => setBonus(e.target.value)} className="w-full rounded-lg border border-[var(--pv-border)] bg-[var(--pv-bg)] px-2 py-1 text-sm font-bold text-[var(--pv-text)] focus:border-cyan-400 focus:outline-none" />
              </div>
            ) : (
              <p data-testid="payment-bonus" className="font-display text-lg font-bold text-[var(--pv-text)]">{fmtMoney(p.bonus || 0)}</p>
            )}
          </div>
          <div className="rounded-2xl bg-cyan-500/10 p-3.5">
            <p className={labelCls}>{owed ? "Total to pay" : "Total paid"}</p>
            <p data-testid="payment-total" className="font-display text-lg font-bold text-cyan-300">{fmtMoney(owed ? total : p.amount)}</p>
          </div>
        </div>
        {owed && (
          <button data-testid="payment-bonus-save" onClick={saveBonus} disabled={busy || (parseFloat(bonus) || 0) === (p.bonus || 0)} className="mt-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3.5 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:opacity-40">
            Save bonus
          </button>
        )}
        {!owed && <p className="mt-2 text-[10px] text-[var(--pv-text-4)]">Finalized — amounts on a paid payment can only change through the reconciliation review.</p>}

        {/* worker payout rails — surfaced in-place so payment handling never needs Discord */}
        {p.worker_id && payout && payout.status !== "unavailable" && (
          <div data-testid="payment-payout-info" className="mt-5 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
            <div className="flex items-center justify-between gap-2">
              <p className={labelCls}>Worker payout details</p>
              {payout.status === "complete" ? (
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Complete</span>
              ) : payout.status === "pending" ? (
                <span data-testid="payout-pending-warning" className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-300">
                  <AlertTriangle className="h-3 w-3" /> Pending your approval
                </span>
              ) : !payout.primary_complete ? (
                <span data-testid="payout-missing-warning" className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-300">
                  <AlertTriangle className="h-3 w-3" /> Primary payment details missing
                </span>
              ) : (
                <span data-testid="payout-missing-warning" className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-2 py-0.5 text-[9px] font-bold uppercase text-[var(--pv-text-4)]">Backup not set (optional)</span>
              )}
            </div>
            <div className="mt-2 space-y-2">
              {[
                ["primary", payout.primary, payout.primary_complete],
                ["backup", payout.backup, payout.backup_complete],
              ].map(([slot, s, complete]) => (
                <div key={slot} data-testid={`payout-slot-${slot}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                  <span className="w-14 shrink-0 font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{slot}</span>
                  {s ? (
                    <>
                      <span className={`font-bold ${complete ? "text-[var(--pv-text)]" : "text-amber-300"}`}>{payout.methods?.[s.method]?.label || s.method}{!complete && " (incomplete)"}</span>
                      <span className="break-all font-medium text-[var(--pv-text-3)]">
                        {slotRows(s, payout.methods).filter((r) => r.value).map((r) => r.value).join(" · ")}
                      </span>
                    </>
                  ) : (
                    <span className="italic text-[var(--pv-text-4)]">not set</span>
                  )}
                </div>
              ))}
            </div>
            {(payout.pending?.primary || payout.pending?.backup) && (
              <p data-testid="payout-pending-note" className="mt-2 flex items-center gap-1.5 text-[10px] font-semibold text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                New {[payout.pending?.primary && "primary", payout.pending?.backup && "backup"].filter(Boolean).join(" + ")} details pending your approval — only the approved method above is ever used.
              </p>
            )}
            {owed && payout.status !== "complete" && (
              <div className="mt-3 border-t border-[var(--pv-border)] pt-3">
                <WorkerPayoutSection workerId={p.worker_id} compact methods={payout.methods} onChanged={() => {}} />
              </div>
            )}
          </div>
        )}

        {/* frozen payout record for paid payments — historical, never follows profile changes */}
        {!owed && p.payout_method && (
          <div data-testid="payment-payout-snapshot" className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className={labelCls}>Paid via (recorded at payment time)</p>
            <p className="mt-1 text-xs">
              <span className="font-bold text-emerald-300">{p.payout_method.label}</span>
              <span className="text-[var(--pv-text-3)]"> — {(p.payout_method.slot === "primary" ? "primary" : "backup")} method on file when paid</span>
            </p>
            <dl className="mt-1.5 space-y-0.5">
              {Object.entries(p.payout_method.fields || {}).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 text-xs">
                  <dt className="shrink-0 font-semibold capitalize text-[var(--pv-text-4)]">{k.replace(/_/g, " ")}</dt>
                  <dd className="break-all text-right font-medium text-[var(--pv-text-2)]">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* payment details */}
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {payout?.primary && (
            <div className="sm:col-span-2">
              <label className={labelCls}>Paid via (snapshots the worker's saved details onto this payment)</label>
              <select data-testid="payment-payout-via" value={payoutVia} onChange={(e) => setPayoutVia(e.target.value)} className={inputCls}>
                <option value="">Manual / other — no snapshot</option>
                {payout.primary && <option value="primary">Primary (recommended) — {payout.methods?.[payout.primary.method]?.label || payout.primary.method}</option>}
                {payout.backup && <option value="backup">Backup — {payout.methods?.[payout.backup.method]?.label || payout.backup.method}</option>}
              </select>
            </div>
          )}
          <div>
            <label className={labelCls}>Payment method</label>
            <select data-testid="payment-method" value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
              <option value="">—</option>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Transaction / reference ID</label>
            <input data-testid="payment-txn" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="e.g. PayPal txn id" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Date sent</label>
            <input data-testid="payment-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Note (optional)</label>
            <input data-testid="payment-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note" className={inputCls} />
          </div>
        </div>
        {!owed && (
          <button data-testid="payment-save-details" onClick={saveDetails} disabled={busy} className="mt-2 rounded-full border border-[var(--pv-border)] px-3.5 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-subtle)] disabled:opacity-40">
            Save details
          </button>
        )}

        {/* proof of payment */}
        <div className="mt-5 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
          <p className={labelCls}>Proof of payment</p>
          {p.has_proof ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-xs font-semibold text-[var(--pv-text-2)]">{p.proof_filename || "receipt"}</span>
              <button data-testid="payment-view-proof" onClick={() => setViewProof(true)} className="flex items-center gap-1 rounded-full bg-cyan-500/15 px-3 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/25">
                <Eye className="h-3 w-3" /> View proof
              </button>
              <label className="flex cursor-pointer items-center gap-1 rounded-full border border-[var(--pv-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30">
                <Upload className="h-3 w-3" /> Replace
                <input data-testid="payment-proof-replace" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { await uploadProof(f); toast.success("Proof replaced."); } catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); } finally { setBusy(false); e.target.value = ""; } }} />
              </label>
              <button data-testid="payment-proof-remove" onClick={removeProof} disabled={busy} className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold text-rose-400 transition-colors hover:bg-rose-500/10">
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          ) : (
            <label data-testid="payment-proof-upload" className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-4 py-4 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40">
              <Upload className="h-4 w-4" /> {proofFile ? proofFile.name : "Upload screenshot or PDF receipt (PNG, JPG, WEBP, PDF — max 15 MB)"}
              <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
            </label>
          )}
          {proofFile && !p.has_proof && owed && <p className="mt-2 text-[10px] text-[var(--pv-text-4)]">Will be attached when you confirm the payment.</p>}
          {proofFile && !p.has_proof && !owed && (
            <button data-testid="payment-proof-save" onClick={async () => { setBusy(true); try { await uploadProof(proofFile); setProofFile(null); toast.success("Proof attached."); } catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); } finally { setBusy(false); } }} disabled={busy} className="mt-2 rounded-full bg-cyan-500 px-3.5 py-1.5 text-[11px] font-bold text-white disabled:opacity-50">
              Attach proof
            </button>
          )}
        </div>

        {/* footer actions */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]">Close</button>
          {owed && (
            <button data-testid="payment-confirm-paid" onClick={markPaid} disabled={busy || !hydrated} className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-xs font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Mark as paid{proofFile ? " + attach proof" : ""} — {fmtMoney(total)}
            </button>
          )}
        </div>
        </>
        )}
      </div>
      {viewProof && p.has_proof && <ProofModal payment={p} onClose={() => setViewProof(false)} />}
    </div>
  );
};
